import { Badge, Tooltip } from "antd-v5";
import Vocabulary from "components/Vocabulary";
import { NOT_DEFINED_OPTION } from "constants/CVSSConstants";
import { groupBy, map } from "lodash";
import React, { Fragment, ReactNode } from "react";

import ACDropdown from "ac-components/ACDropdown";
import ACButton from "ac-components/ac-button/ACButton";
import ACSegmented from "ac-components/ac-segmented/ACSegmented";

type CVSSFieldOption = {
  label: string;
  value: string;
};

type CVSSField = {
  name: string;
  label: string;
  category: string;
  subCategory: string;
  options: CVSSFieldOption[];
};

type CVSSFormFieldsProps = {
  subtitle: ReactNode;
  handleCvssChange: (value: string, fieldName: string) => void;
  inheritedCvssValues?: { [key: string]: string };
  showHeader: boolean;
  fields: CVSSField[];
  cvssObject: { [key: string]: string };
  includeNotDefined?: boolean;
  middleColumnTitle?: string;
  handleBaseChange?: (value: string, fieldName: string) => void;
  showBaseMetricsColumn?: boolean;
  baseMetricsValues?: { [key: string]: string };
  firstColumnWidth?: number;
  middleColumnWidth?: number;
  viewMode?: boolean;
  editableModifiersFor3v1?: boolean;
  inheritedCVSSTooltip?: string;
};

export default function CVSSFormFields({
  subtitle,
  handleCvssChange,
  inheritedCvssValues,
  showHeader,
  fields,
  cvssObject,
  includeNotDefined = true,
  middleColumnTitle,
  handleBaseChange, // This is the optional handler for the middle column
  showBaseMetricsColumn,
  baseMetricsValues = {},
  firstColumnWidth = 260,
  middleColumnWidth = 180,
  viewMode,
  editableModifiersFor3v1,
  inheritedCVSSTooltip,
}: CVSSFormFieldsProps) {
  return (
    <div className="cvss-metrics-wrapper">
      {subtitle && <div className="cvss-mtrics-info-message">{subtitle}</div>}
      <div className="cvss-metric-list">
        {Object.entries(groupBy(fields, "category")).map(
          ([category, categoryFields], categoryIndex) => {
            return (
              <Fragment key={category}>
                {category && (
                  <div className="cvss-section-title">{category}</div>
                )}

                {Object.entries(groupBy(categoryFields, "subCategory")).map(
                  ([subCategory, subCategoryFields], subCategoryIndex) => {
                    return (
                      <Fragment key={subCategory}>
                        {subCategory && (
                          <div className="cvss-section-sub-title">
                            {subCategory}
                          </div>
                        )}
                        <div
                          className="cvss-metric-list-grid"
                          data-columns={showBaseMetricsColumn ? "3" : "2"}
                        >
                          {showHeader &&
                            categoryIndex === 0 &&
                            subCategoryIndex === 0 && (
                              <>
                                <div
                                  className="cvss-grid-colhead"
                                  style={{ width: firstColumnWidth }}
                                >
                                  Metric
                                </div>
                                {middleColumnTitle && (
                                  <div
                                    className="cvss-grid-colhead"
                                    style={{ width: middleColumnWidth }}
                                  >
                                    {middleColumnTitle}
                                  </div>
                                )}
                                <div className="cvss-grid-colhead">
                                  Environmental
                                </div>
                              </>
                            )}
                          {map(subCategoryFields, (field) => {
                            // Check for inherited value to show the badge
                            const inheritedValue =
                              inheritedCvssValues?.[field.name];
                            const baseMetricKey = showBaseMetricsColumn
                              ? field.name.replace(/^M/, "")
                              : field.name.replace(/\d+$/, ""); //removing any numbers from the end of the string

                            const baseValue = baseMetricsValues[baseMetricKey];
                            const baseValueLabel =
                              field.options.find(
                                (opt) => opt.value === baseValue,
                              )?.label || "Not Defined";
                            const modifiedOptions = [
                              ...(includeNotDefined
                                ? [NOT_DEFINED_OPTION]
                                : []),
                              ...field.options,
                            ].map((option) => {
                              const isInherited =
                                inheritedValue &&
                                inheritedValue === option.value;
                              return {
                                ...option,
                                label: isInherited ? (
                                  <>
                                    <Tooltip
                                      title={
                                        inheritedCVSSTooltip ? (
                                          inheritedCVSSTooltip
                                        ) : (
                                          <>
                                            From <Vocabulary name="product" />{" "}
                                            Modifiers
                                          </>
                                        )
                                      }
                                    >
                                      <Badge color="#97989B" />
                                    </Tooltip>{" "}
                                    {option.label}
                                  </>
                                ) : (
                                  option.label
                                ),
                              };
                            });
                            const isSpecialSection =
                              field.subCategory ===
                                "Impact Subscore Modifiers" ||
                              field.category === "Temporal Score Metrics";
                            return (
                              <Fragment key={field.name}>
                                <div
                                  className="cvss-metric-list-item-label"
                                  style={{ width: firstColumnWidth }}
                                >
                                  {field.label}&nbsp;
                                  <span className="color-foreground-muted">
                                    ({baseMetricKey})
                                  </span>
                                </div>
                                {showBaseMetricsColumn && handleBaseChange && (
                                  <div
                                    className="cvss-metric-list-item-value base-metric-display"
                                    style={{ width: middleColumnWidth }}
                                  >
                                    {isSpecialSection ? null : ( // Render nothing for these sections to keep the column empty.
                                      <ACDropdown
                                        items={field.options}
                                        onOptionClick={(value) =>
                                          handleBaseChange(
                                            String(value),
                                            baseMetricKey,
                                          )
                                        }
                                        disabled={viewMode}
                                      >
                                        <ACButton
                                          type="outline"
                                          disabled={viewMode}
                                        >
                                          {baseValueLabel}
                                        </ACButton>
                                      </ACDropdown>
                                    )}
                                  </div>
                                )}
                                <div className="cvss-metric-list-item-value">
                                  <ACSegmented
                                    items={modifiedOptions}
                                    onChange={(value) =>
                                      handleCvssChange(
                                        String(value),
                                        field.name,
                                      )
                                    }
                                    value={
                                      cvssObject[field.name] ||
                                      (includeNotDefined
                                        ? NOT_DEFINED_OPTION.value
                                        : "")
                                    }
                                    //Enable modifiers option values for 3.1 View Mode
                                    disabled={
                                      viewMode && !editableModifiersFor3v1
                                    }
                                    style={{ whiteSpace: "nowrap" }}
                                  />
                                </div>
                              </Fragment>
                            );
                          })}
                        </div>
                      </Fragment>
                    );
                  },
                )}
              </Fragment>
            );
          },
        )}
      </div>
    </div>
  );
}
